/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useState, useRef, useEffect } from 'react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Card } from '@/components/ui/card'
import { Bot, Send, User, AlertTriangle, Check, X } from 'lucide-react'

interface Message {
  role: 'user' | 'assistant'
  content: string
  toolCalls?: any[]
  toolResults?: string
}

interface PendingAction {
  tool: string
  tool_call_id: string
  args: any
  summary: string
}

function simplifyToolCalls(toolCalls: any[]): any[] {
  const merged = new Map<string, any>()
  for (const tc of toolCalls || []) {
    const id = tc.id || tc.index
    if (!merged.has(id)) {
      merged.set(id, { ...tc, function: { ...tc.function, arguments: '' } })
    }
    if (tc.function?.arguments) {
      const existing = merged.get(id)!
      existing.function.arguments += tc.function.arguments
    }
  }
  return Array.from(merged.values())
}

function describeAction(toolCalls: any[]): PendingAction[] {
  return toolCalls.map((tc) => {
    let args: any = {}
    try {
      args = JSON.parse(tc.function?.arguments || '{}')
    } catch {}
    const toolName = tc.function?.name || ''
    let summary = ''
    switch (toolName) {
      case 'update_setting':
        summary = `修改设置 ${args.key} = ${args.value}`
        break
      case 'update_channel_status':
        summary = `${args.enabled ? '启用' : '禁用'}渠道 #${args.id}`
        break
      case 'update_model_price':
        summary = `修改模型 ${args.model} 倍率`
        break
      case 'restart_service':
        summary = '重启服务'
        break
      default:
        summary = `执行 ${toolName}`
    }
    return { tool: toolName, tool_call_id: tc.id, args, summary }
  })
}

export function AiAssistant() {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([])
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  const handleSend = async () => {
    if (!input.trim() || loading) return

    const userMsg: Message = { role: 'user', content: input }
    const newMessages = [...messages, userMsg]
    setMessages(newMessages)
    setInput('')
    setLoading(true)
    setPendingActions([])

    try {
      const msgs = newMessages.map((m) => ({ role: m.role, content: m.content }))
      const res = await fetch('/api/admin/assistant/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: msgs }),
      })

      if (!res.ok) {
        setMessages([...newMessages, { role: 'assistant', content: '请求失败: ' + res.status }])
        setLoading(false)
        return
      }

      const reader = res.body?.getReader()
      if (!reader) return

      const decoder = new TextDecoder()
      let fullContent = ''
      let toolCalls: any[] = []

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const text = decoder.decode(value, { stream: true })
        const lines = text.split('\n')

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6).trim()
          if (data === '[DONE]') continue

          try {
            const parsed = JSON.parse(data)

            if (parsed.type === 'tool_calls') {
              toolCalls = simplifyToolCalls(parsed.tool_calls || [])
              continue
            }

            const choice = parsed.choices?.[0]
            if (choice?.delta?.content) {
              fullContent += choice.delta.content
            }
            if (choice?.delta?.tool_calls) {
              toolCalls = simplifyToolCalls(choice.delta.tool_calls)
            }
          } catch {}
        }
      }

      const assistantMsg: Message = { role: 'assistant', content: fullContent || '(思考中...)' }

      if (toolCalls.length > 0) {
        assistantMsg.toolCalls = toolCalls
        assistantMsg.content = fullContent || `正在准备执行以下操作...`
        setPendingActions(describeAction(toolCalls))
      }

      setMessages([...newMessages, assistantMsg])
    } catch (e: any) {
      setMessages([...newMessages, { role: 'assistant', content: '错误: ' + e.message }])
    }
    setLoading(false)
  }

  const handleConfirm = async (action: PendingAction, confirm: boolean) => {
    if (confirm) {
      try {
        const res = await api.post('/api/admin/assistant/confirm', {
          action: action.tool,
          args: action.args,
        })
        const result = res.data?.data
        const resultStr = result ? JSON.stringify(result, null, 2) : '已执行'

        setMessages((prev) => {
          const last = [...prev]
          const msg = last[last.length - 1]
          if (msg.role === 'assistant') {
            msg.toolResults = resultStr
          }
          return last
        })
      } catch (e: any) {
        setMessages((prev) => {
          const last = [...prev]
          const msg = last[last.length - 1]
          if (msg.role === 'assistant') {
            msg.toolResults = '执行失败: ' + e.message
          }
          return last
        })
      }
    }

    setPendingActions([])
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b px-6 py-3">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <Bot className="size-5" />
          AI 管理员助手
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          模型: Auto（自动选择最优模型）。所有修改操作需经你确认后执行
        </p>
      </div>

      <ScrollArea ref={scrollRef} className="flex-1 p-6">
        <div className="mx-auto max-w-3xl space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-muted-foreground py-12">
              <Bot className="mx-auto size-12 mb-3 opacity-50" />
              <p>欢迎使用 AI 管理员助手！</p>
              <p className="text-sm mt-1">你可以让我查看系统设置、管理渠道、调整模型定价等</p>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
              {msg.role === 'assistant' && (
                <div className="size-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Bot className="size-4 text-primary" />
                </div>
              )}

              <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-first' : ''}`}>
                <Card className={`p-3 ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-card'}`}>
                  <p className="whitespace-pre-wrap text-sm">{msg.content}</p>

                  {msg.toolCalls && msg.toolCalls.length > 0 && pendingActions.length > 0 && (
                    <div className="mt-3 space-y-2 border-t pt-3">
                      <p className="text-xs font-medium text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="size-3" /> 需要确认：
                      </p>
                      {pendingActions.map((action, j) => (
                        <div key={j} className="flex items-center justify-between bg-amber-50 dark:bg-amber-950/30 rounded p-2 text-xs">
                          <span>{action.summary}</span>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="h-6 px-2 text-green-600" onClick={() => handleConfirm(action, true)}>
                              <Check className="size-3 mr-1" /> 允许
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 px-2 text-red-600" onClick={() => handleConfirm(action, false)}>
                              <X className="size-3 mr-1" /> 拒绝
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {msg.toolResults && (
                    <div className="mt-2 border-t pt-2">
                      <p className="text-xs font-medium text-green-600 mb-1">执行结果：</p>
                      <pre className="text-xs bg-muted p-2 rounded whitespace-pre-wrap">{msg.toolResults}</pre>
                    </div>
                  )}
                </Card>
              </div>

              {msg.role === 'user' && (
                <div className="size-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                  <User className="size-4" />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-3">
              <div className="size-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Bot className="size-4 text-primary" />
              </div>
              <Card className="p-3 bg-card">
                <div className="flex gap-1">
                  <div className="size-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="size-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="size-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </Card>
            </div>
          )}
        </div>
      </ScrollArea>

      <div className="border-t p-4">
        <div className="mx-auto max-w-3xl flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            placeholder="输入你想让管理员助手的操作..."
            className="min-h-[44px] max-h-[120px]"
            disabled={loading}
          />
          <Button onClick={handleSend} disabled={loading || !input.trim()} className="shrink-0">
            <Send className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
